import * as React from 'react';
import styled, { keyframes } from 'styled-components';

import { Obj as Obj_, useSize, usePosition, TracePosition, TraceSize } from '../../core/Trace';
import { useIORef } from '../../core/hooks';
import { observeEffect } from '../utils';
import { Animation, Animate } from '../Animation';
import * as Modal from '../Modal';
import { useStacked } from '../Keyboard';

const Obj = Modal.withPortalModal(Obj_);


export const Spacer = styled.span`
    display: inline-block;
    width: 1rem;
`;

interface ContextProps {
    innerRef: React.RefObject<HTMLElement>,
    isShow: boolean,
    position: TracePosition,
    size: TraceSize,
    animation: Animate,
    resetable?: boolean,
    onOut?: ({ 
        updatePosition, 
        updateSize,
    }: { 
        updatePosition: (x: TracePosition) => void, 
        updateSize: (x: TraceSize) => void,
    }, e: IntersectionObserverEntry) => void,
    onIn?: () => void,
    id: string,
}

const contextStyle = (isShow: boolean): React.CSSProperties => ({ pointerEvents: isShow ? undefined : 'none' });

export const Context: React.FunctionComponent<ContextProps> = ({ 
    innerRef, 
    isShow,
    position, 
    size, 
    animation,
    resetable = false,
    onOut = () => {},
    onIn = () => {}, 
    children,
    id,
}) => {
    const { modalId, isActual: inModal, showed } = Modal.useContext();

    const isShowable = inModal ? showed && isShow : isShow;

    const { 
        read: pos,
        write: updatePosition,
    } = useIORef(position);

    const { 
        read: s,
        write: updateSize,
    } = useIORef(size);

    const [, {
        getResult: getPosition, 
        onCalc: onCalcPosition, 
        offCalc: offCalcPosition,
    }] = usePosition({
        target: innerRef,
        relative: inModal ? 'screen' : 'document',
        position: pos(),
    });

    const [, {
        getResult: getSize,
        onCalc: onCalcSize,
        offCalc: offCalcSize,
    }] = useSize({
        target: innerRef,
        offset: getPosition,
        takeWidth: s(),
    });

    

    React.useEffect(() => {
        updatePosition(position)();
    }, [position]);


    React.useEffect(() => {
        updateSize(size)();
    }, [size]);


    React.useEffect(() => {
        if(isShowable){
            onCalcPosition();
            onCalcSize();
        }
        else {
            offCalcPosition();
            offCalcSize();
        }
    }, [isShowable]);



    const intersectParent = React.useMemo(() => {
        return new IntersectionObserver((entries) => {
            if(entries[0].intersectionRatio === 0){
                onOut({ updatePosition, updateSize }, entries[0]);
            }
            else {
                onIn();
            }
        })
    }, []);


    React.useEffect(() => {
        const r = observeEffect(innerRef, intersectParent);

        return r();
    }, []);

    useStacked(isShowable, id);

    const { top, left } = getPosition();

    return (
        <Obj 
            className={'popuper'}
            style={{
                top,
                left: resetable ? 0 : left,
                width: getSize().width,
                ...contextStyle(isShowable),
            }} 
            {...modalId}>
            <Animation style={animation(isShowable)}>
                {children}
            </Animation>
        </Obj>
    );
}

type AnimationProps = {
    isShow: boolean,
    className?: string,
}

export const AnimationBlock: React.FunctionComponent<AnimationProps> = ({ children, isShow, className }) => {
    const [isShowed, updateState] = React.useState(false);

    const handler = () => {
        if(!isShow){
            updateState(false);
        }
    }

    React.useEffect(() => {
        if(isShow){
            updateState(isShow);
        }
    }, [isShow]);

    return isShowed ? <Box isShow={isShow} className={className} onAnimationEnd={handler}>{children}</Box> : null;
}

const showAnimation = keyframes`
    0%   { transform: translateY(-5px); opacity: 0; };
    100% { transform: translateY(0px);  opacity: 1; };
`;

const hideAnimation = keyframes`
    0%   { transform: translateY(0px);  opacity: 1; };
    100% { transform: translateY(-5px); opacity: 0; };
`;

const Box = styled.div<AnimationProps>`
    animation-timing-function: ease;
    animation-duration: .33s;
    animation-fill-mode: forwards;
    animation-name: ${({ isShow }) => isShow ? showAnimation : hideAnimation };
    width: 100vw;
    height: 100vh;
    transition: all .33s ease;
`;
